// ============================================================
// VidyaTrack — Notification Center
// Real-time via Firebase Realtime Database
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from "react";
import { FiBell, FiCheck, FiBook, FiAlertCircle, FiCalendar, FiCheckSquare, FiBellOff } from "react-icons/fi";
import { ref, onValue, off, update } from "firebase/database";
import { rtdb } from "../../firebase/config";
import { useAuth } from "../../contexts/AuthContext";
import { formatDistanceToNow } from "date-fns";

const TYPE_CFG = {
  homework  : { icon:<FiBook size={13}/>,        bg:"#e8eef8", color:"#054bb4" },
  notice    : { icon:<FiAlertCircle size={13}/>, bg:"#fef2f2", color:"#dc2626" },
  attendance: { icon:<FiCheckSquare size={13}/>, bg:"#ecfdf5", color:"#059669" },
  event     : { icon:<FiCalendar size={13}/>,    bg:"#fffbeb", color:"#d97706" },
  submission: { icon:<FiBook size={13}/>,         bg:"#f5f3ff", color:"#7c3aed" },
};
const cfg = (type) => TYPE_CFG[type] || { icon:<FiBell size={13}/>, bg:"#f5f3ff", color:"#7c3aed" };

export default function NotificationCenter() {
  const { userData, currentUser } = useAuth();
  const [notifs,  setNotifs]  = useState([]);
  const [open,    setOpen]    = useState(false);
  const [ringing, setRinging] = useState(false);
  const panelRef = useRef();
  const prevUnread = useRef(0);

  const uid     = currentUser?.uid;
  const role    = userData?.role;
  const classId = userData?.classId;

  // Determine Realtime DB path based on role
  const rtPath = role === "teacher"
    ? `notifications/teacher_${uid}`
    : role === "admin"
    ? "notifications/admin"
    : classId ? `notifications/class_${classId}` : null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!uid || !rtPath || rtPath.endsWith('_null') || rtPath.endsWith('_undefined')) return;
    const r = ref(rtdb, rtPath);
    onValue(r, snap => {
      const val = snap.val();
      if (val) {
        const arr = Object.entries(val)
          .map(([id, v]) => ({ id, ...v }))
          .sort((a, b) => (b.ts || 0) - (a.ts || 0))
          .slice(0, 30);
        setNotifs(arr);
      }
    });
    return () => off(r);
  }, [uid, rtPath]);

  // Ring bell on new unread
  const unread = notifs.filter(n => !n.read).length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (unread > prevUnread.current) { setRinging(true); setTimeout(() => setRinging(false), 700); }
    prevUnread.current = unread;
  }, [unread]);

  // Close on outside click
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = e => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const markRead = useCallback((id) => {
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    if (uid && rtPath) update(ref(rtdb, `${rtPath}/${id}`), { read: true }).catch(() => {});
  }, [uid, rtPath]);

  const markAllRead = useCallback(() => {
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    if (uid && rtPath) {
      const updates = {};
      notifs.filter(n => !n.read).forEach(n => { updates[`${rtPath}/${n.id}/read`] = true; });
      if (Object.keys(updates).length) update(ref(rtdb, "/"), updates).catch(() => {});
    }
  }, [uid, rtPath, notifs]);

  return (
    <div ref={panelRef} style={{ position:"relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        className={ringing ? "bell-ring" : ""}
        aria-label="Notifications"
        style={{
          width:36, height:36,
          background: open ? "var(--blue-100)" : "var(--gray-100)",
          border:"none", borderRadius:"var(--r6)",
          display:"flex", alignItems:"center", justifyContent:"center",
          cursor:"pointer", position:"relative", transition:"var(--t)",
          color: open ? "var(--blue-700)" : "var(--gray-500)",
        }}
        onMouseEnter={e => { if(!open) e.currentTarget.style.background="var(--blue-100)"; }}
        onMouseLeave={e => { if(!open) e.currentTarget.style.background="var(--gray-100)"; }}
      >
        <FiBell size={17}/>
        {unread > 0 && (
          <div style={{ position:"absolute", top:5, right:5, width:7, height:7, background:"var(--red-600)", borderRadius:"50%", border:"2px solid #fff" }}/>
        )}
      </button>

      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 8px)", right:0,
          width:330, maxHeight:440,
          background:"#fff", border:"1px solid var(--border)",
          borderRadius:"var(--r12)", boxShadow:"0 10px 32px rgba(0,0,0,.10)",
          zIndex:400, display:"flex", flexDirection:"column",
          animation:"fadeIn .15s ease", overflow:"hidden",
        }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 14px", background:"var(--blue-50, #f4f7fd)", borderBottom:"1px solid var(--border)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontWeight:700, fontSize:".88rem" }}>Notifications</span>
              {unread > 0 && (
                <span style={{ background:"var(--red-600)", color:"#fff", fontSize:".65rem", fontWeight:800, padding:"1px 7px", borderRadius:20 }}>{unread}</span>
              )}
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} style={{ background:"none", border:"none", cursor:"pointer", fontSize:".72rem", color:"var(--blue-700)", fontWeight:600, display:"flex", alignItems:"center", gap:4 }}>
                <FiCheck size={12}/> Mark all read
              </button>
            )}
          </div>

          <div style={{ overflowY:"auto", flex:1 }}>
            {notifs.length === 0 ? (
              <div style={{ padding:"32px 16px", textAlign:"center", color:"var(--gray-400)" }}>
                <FiBellOff size={26} style={{ margin:"0 auto 8px", display:"block" }}/>
                <div style={{ fontSize:".84rem" }}>No notifications yet</div>
              </div>
            ) : notifs.map((n, i) => {
              const c = cfg(n.type);
              return (
                <div key={n.id} onClick={() => markRead(n.id)} style={{
                  display:"flex", gap:10, alignItems:"flex-start",
                  padding:"11px 14px",
                  borderBottom: i < notifs.length - 1 ? "1px solid #f0f0f0" : "none",
                  background: n.read ? "#fff" : "var(--blue-50, #f4f7fd)",
                  cursor:"pointer", transition:"background var(--t)",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--gray-100)"}
                onMouseLeave={e => e.currentTarget.style.background = n.read ? "#fff" : "var(--blue-50, #f4f7fd)"}
                >
                  <div style={{ width:30, height:30, borderRadius:"var(--r6)", background:c.bg, color:c.color, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{c.icon}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:".82rem", fontWeight:n.read?500:700, color:"var(--gray-900)", marginBottom:2 }}>{n.title}</div>
                    {n.body && <div style={{ fontSize:".74rem", color:"var(--gray-500)", marginBottom:3 }}>{n.body}</div>}
                    <div style={{ fontSize:".7rem", color:"var(--gray-400)" }}>
                      {n.ts ? formatDistanceToNow(new Date(n.ts), { addSuffix:true }) : "recently"}
                    </div>
                  </div>
                  {!n.read && <div style={{ width:7, height:7, borderRadius:"50%", background:"var(--blue-700)", flexShrink:0, marginTop:5 }}/>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
